<script setup lang="ts">
import { COLOR_WHITE } from '@/constants';
import { computed, ref, useTemplateRef } from 'vue';
import { EPosition } from 'common/enum/misc.enum';
import PositionablePopover from '../dialogs/PositionablePopover.vue';

const props = withDefaults(
  defineProps<{
    colors: (string | { value: string; label: string })[];
    modelValue?: string | null;
    label?: string;
    compact?: boolean;
    position?: EPosition;
    offsetY?: number;
    flipStrategy?: 'both' | 'horizontal' | 'vertical';
  }>(),
  {
    modelValue: null,
    label: undefined,
    compact: false,
    position: EPosition.BOTTOM_LEFT,
    offsetY: 0,
    flipStrategy: 'horizontal',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', color: string): void;
}>();

const menuVisible = ref(false);
const selected = computed(() => props.modelValue?.toUpperCase() ?? '');
const options = computed(() =>
  props.colors.map((color) => (typeof color === 'string' ? { value: color, label: color } : color)),
);
const buttonRef = useTemplateRef<HTMLButtonElement>('buttonRef');
const dialogRef = useTemplateRef('dialogRef');

const hide = (): void => {
  menuVisible.value = false;
  dialogRef.value?.hide();
};

const show = (): void => {
  menuVisible.value = true;
  void dialogRef.value?.show();
};

const toggle = (): void => {
  if (menuVisible.value) {
    hide();
    return;
  }

  show();
};

const selectColor = (color: string): void => {
  emit('update:modelValue', color);
  hide();
  buttonRef.value?.focus({ preventScroll: true });
};
</script>

<template>
  <div class="relative flex items-center justify-center">
    <button
      ref="buttonRef"
      type="button"
      class="relative flex shrink-0 items-center justify-center rounded-full border outline-inner"
      :class="[
        compact
          ? 'size-icon-xl border-divider'
          : 'size-cell-md border-quaternary transition duration-300 hover:border-primary active:border-primary hover:border-2 active:border-2',
        !compact && menuVisible && '!border-primary !border-2',
      ]"
      :aria-label="label ?? modelValue ?? undefined"
      :aria-expanded="menuVisible"
      aria-haspopup="dialog"
      @click.stop="toggle"
    >
      <span
        class="rounded-full pointer-events-none"
        :class="[compact ? 'size-[28px]' : 'size-icon-button', selected === COLOR_WHITE && 'border border-divider']"
        :style="{ backgroundColor: selected }"
      ></span>
    </button>

    <PositionablePopover
      ref="dialogRef"
      :position="position"
      :anchor-element="buttonRef"
      :offset-y="offsetY"
      :flip-strategy="flipStrategy"
      role="dialog"
      :aria-label="label ?? modelValue ?? undefined"
      @hide="menuVisible = false"
    >
      <div class="bg-surface p-md rounded shadow-md">
        <div class="grid grid-cols-6 gap-sm w-[232px]">
          <button
            v-for="color in options"
            :key="color.value"
            type="button"
            class="relative flex size-icon-button items-center justify-center rounded-full outline-inner"
            :class="[
              'transition duration-300 hover:!bg-primary/20 active:!bg-primary/20',
              selected === color.value.toUpperCase() && 'outline outline-tint',
            ]"
            :aria-label="color.label"
            :title="color.label"
            :aria-pressed="selected === color.value.toUpperCase()"
            @click="selectColor(color.value)"
          >
            <span
              class="rounded-full pointer-events-none"
              :class="[
                'size-icon-md transition duration-300',
                color.value.toUpperCase() === COLOR_WHITE && 'border border-divider',
              ]"
              :style="{ backgroundColor: color.value }"
            ></span>
          </button>
        </div>
      </div>
    </PositionablePopover>
  </div>
</template>
